import { FC, ReactNode, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { FloatingTabs, Tooltip } from '@dbt-labs/sourdough';

import { useUrlProjectParams } from '@/hooks/params';
import { useQueryParamOption } from '@/hooks/queryParamOption';
import { useRelationshipLinks } from '@/hooks/useRelationshipLinks';
import { ParsedUniqueId, parseUniqueId } from '@/typings/parseUniqueId';
import { buildTypePredicate } from '@/util/type';

interface PanelTabsProps {
  children: (tabType: TabType) => ReactNode;
  isColumnsTabEnabled: boolean;
  showColumns: boolean;
  showRelationships: boolean;
}

const tabTypes = ['general', 'columns', 'relationships'] as const;
type TabType = (typeof tabTypes)[number];

type TabInfo = {
  type: TabType;
  count?: number;
  isDisabled?: boolean | undefined;
  disabledReason?: string;
};

const tabNameMap: Record<TabType, string> = {
  general: 'General',
  columns: 'Columns',
  relationships: 'Relationships',
};

const isTabType = buildTypePredicate(tabTypes);

const getTabType = (input: string | undefined | null): TabType | null => {
  return isTabType(input) ? input : null;
};

export const ResourcePanelTabs: FC<PanelTabsProps> = (props: PanelTabsProps) => {
  const { children, isColumnsTabEnabled, showColumns, showRelationships } = props;
  const [tabParam, setTabParam] = useQueryParamOption({
    param: 'tab',
    options: tabTypes,
    validateOption: getTabType,
  });

  const [searchParams] = useSearchParams();
  const uniqueId = useMemo((): ParsedUniqueId | null => {
    const uniqueId = searchParams.get('uniqueId');

    return uniqueId ? parseUniqueId(uniqueId) : null;
  }, [searchParams]);

  const projectScope = useUrlProjectParams();

  const { links } = useRelationshipLinks({
    uniqueId,
    projectScope,
    onlyReferencedByProjects: true,
  });
  // TODO[jcserv]: Columns tab should be disabled if user is unable to query columns from that resource.
  const tabs: TabInfo[] = [{ type: 'general' }];
  if (showColumns) {
    tabs.push({
      type: 'columns',
      isDisabled: !isColumnsTabEnabled,
      disabledReason: 'You do not have access to the project that owns this',
    });
  }
  if (showRelationships) {
    tabs.push({ type: 'relationships', count: links?.length });
  }

  const activeTab = tabParam ? { type: tabParam } : tabs[0];

  return (
    <div>
      <div className="overflow-x-auto">
        <FloatingTabs>
          {tabs.map((tabDetails) => (
            <FloatingTabs.Tab
              key={tabNameMap[tabDetails.type]}
              id={tabDetails.type}
              isActive={activeTab.type === tabDetails.type}
              onClick={(tab: TabType) => {
                if (tabDetails.isDisabled) {
                  return;
                }
                setTabParam(tab);
              }}
              count={tabDetails.count}
              isDisabled={tabDetails.isDisabled ?? false}
              disabledReason={tabDetails.disabledReason ?? ''}
            >
              <Tooltip
                content={tabDetails.disabledReason}
                hidden={!tabDetails.isDisabled}
              >
                {tabNameMap[tabDetails.type]}
              </Tooltip>
            </FloatingTabs.Tab>
          ))}
        </FloatingTabs>
      </div>

      <>{activeTab.type && <div>{children(activeTab.type)}</div>}</>
    </div>
  );
};
